import { Alert } from 'antd';
import { AndroidSettings, IOSSettings, NativeSettings } from 'capacitor-native-settings';
import NotificationSuggester from 'components/cards/NotificationSuggester';
import PostsList from 'components/PostsList';
import dayjs from 'dayjs';
import useCheckNotificationsPermissions from 'hooks/useCheckNotificationsPermissions';
import usePreferences from 'hooks/usePreferences';
import useScheduleArticleNotifications, {
  NOTIFICATION_ARTICLES,
  PREVENTION_PROGRAM_INTERVAL_DAYS,
} from 'hooks/useScheduleArticleNotifications';
import { PREVENTION_JOURNEY_COUNT } from 'notifications';
import posthog from 'posthog-js';
import Keys from 'preferences/preferences';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PostMetadataDTO } from 'services/api/models/PostDTO';
import { routes } from 'services/RouteService';
import { selectSuggestedPosts } from 'store/content/selectors';
import Platform from 'utils/Platform';
import checkupCover from '../../assets/checkup-cover.jpg';
import ModuleCard from './ModuleCard';
import { Button } from 'components/ui';
import { colors } from 'design';

const useStyles = createUseStyles({
  description: {
    marginTop: 12,
    marginBottom: 16,
    color: colors.text.tertiary,
  },
  articlesContainer: {
    marginTop: 16,
  },
  infoText: {
    marginTop: 12,
    color: colors.text.tertiary,
    marginBottom: 0,
  },
  notificationSuggester: {
    marginTop: 16,
  },
  notificationsRequiredContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    alignItems: 'center',
    textAlign: 'center',
  },
});

export default function PreventionJourneyModule() {
  const styles = useStyles();
  const { t } = useTranslation();
  const suggestedPosts = useSelector(selectSuggestedPosts);

  // Preference-backed journey state so it persists across sessions
  const { value: rawPrefs, set: setPrefs, loading: loadingPrefs } = usePreferences(Keys.PREVENTION_JOURNEY);

  const navigate = useNavigate();
  const { doesUserHaveNotifications } = useCheckNotificationsPermissions();
  const { scheduleInitialArticleReminders } = useScheduleArticleNotifications();
  const hasStarted = Boolean(rawPrefs?.startDate);
  const startDate = rawPrefs?.startDate ? dayjs(rawPrefs.startDate) : undefined;

  // When user clicks "Commencer" without notifications, we show a warning and wait for them to enable notifications
  const [showNotificationsRequired, setShowNotificationsRequired] = useState(false);

  const startPreventionJourney = useCallback(() => {
    const today = dayjs().set('hour', 0).set('minute', 0).set('second', 0).set('millisecond', 0).toISOString();
    setPrefs(JSON.stringify({ startDate: today }));
    scheduleInitialArticleReminders();
    setShowNotificationsRequired(false);
    posthog.capture('prevention.journey_started');
  }, [setPrefs, scheduleInitialArticleReminders]);

  // When coming back from notification settings, if notifications are now enabled, start the journey
  useEffect(() => {
    if (doesUserHaveNotifications && showNotificationsRequired) {
      startPreventionJourney();
    }
  }, [doesUserHaveNotifications, showNotificationsRequired, startPreventionJourney]);

  const currentArticleIndex = useMemo(() => {
    if (!startDate) return 0;
    const daysSinceStart = dayjs().diff(startDate, 'day');
    // Clamp between 0 and ARTICLE_READ_REMINDER_COUNT - 1 (for posts 1..ARTICLE_READ_REMINDER_COUNT)
    return Math.max(
      0,
      Math.min(PREVENTION_JOURNEY_COUNT - 1, Math.floor(daysSinceStart / PREVENTION_PROGRAM_INTERVAL_DAYS)),
    );
  }, [startDate]);

  // Build a small fixed list of posts with ids 1..4 from existing metadata,
  // falling back to minimal placeholders if some are missing.
  const preventionPosts: PostMetadataDTO[] = useMemo(() => {
    const ids = NOTIFICATION_ARTICLES.map(article => article.id);

    return ids.map(id => {
      const existing = suggestedPosts?.find(p => p.id === id);
      if (existing) return existing;

      return {
        id,
        title: t('preventionJourney.articleFallbackTitle', { id }),
        cover_image_url: undefined,
        read: false,
        is_new: false,
        // other fields are not used by PostsList / navigation
      } as unknown as PostMetadataDTO;
    });
  }, [suggestedPosts, t]);

  const activePost = preventionPosts[currentArticleIndex] ?? preventionPosts[0];
  const hasReadActivePost = Boolean(activePost?.read);
  const isFinalPost = currentArticleIndex === PREVENTION_JOURNEY_COUNT - 1;

  return (
    <ModuleCard title={t('preventionJourney.title')} coverImage={checkupCover}>
      {currentArticleIndex < 2 && <p className={styles.description}>{t('preventionJourney.description')}</p>}
      {!hasStarted && !showNotificationsRequired && (
        <Button
          variant="primary"
          block
          size="lg"
          onClick={() => {
            posthog.capture('prevention.start_journey_clicked');
            if (doesUserHaveNotifications) {
              startPreventionJourney();
            } else {
              setShowNotificationsRequired(true);
            }
          }}
          loading={loadingPrefs}
        >
          {t('preventionJourney.start')}
        </Button>
      )}
      {!hasStarted && showNotificationsRequired && (
        <Alert
          type="warning"
          style={{ width: '100%' }}
          message={
            <div className={styles.notificationsRequiredContainer}>
              <p>{t('preventionJourney.notificationsRequired')}</p>
              <Button
                variant="highlight"
                style={{ marginBottom: 12 }}
                onClick={() => {
                  if (Platform.isNative()) {
                    NativeSettings.open({
                      optionAndroid: AndroidSettings.AppNotification,
                      optionIOS: IOSSettings.App,
                    });
                  } else {
                    navigate(routes.settings.route);
                  }
                  posthog.capture('notification_suggester_clicked');
                }}
              >
                {t('preventionJourney.notificationSettings')}
              </Button>
            </div>
          }
        />
      )}
      {hasStarted && (
        <div className={styles.articlesContainer}>
          <PostsList
            posts={activePost ? [activePost] : []}
            extraReadMarkers
            posthogOnArticleOpenExtras={{ opened_on_page: 'prevention_journey' }}
          />
          {hasReadActivePost && (
            <p className={styles.infoText}>
              {isFinalPost ? t('preventionJourney.journeyComplete') : t('preventionJourney.nextArticle')}
            </p>
          )}
        </div>
      )}
      {!doesUserHaveNotifications && hasStarted && (
        <div className={styles.notificationSuggester}>
          <NotificationSuggester text={t('preventionJourney.enableNotificationsSuggestion')} />
        </div>
      )}
    </ModuleCard>
  );
}
